'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    createPipelineDistributionGroup,
    deletePipelineDistributionGroup,
    getPipelineDistributionGroups,
    getPipelineOwnerCandidates,
    PipelineDistributionGroup,
    PipelineOwnerCandidate,
    updatePipelineDistributionGroup,
} from '@/lib/api/sales'
import { useEffect, useState } from 'react'
import { MdAdd, MdCheck, MdClose, MdDelete, MdEdit, MdShuffle } from 'react-icons/md'
import { toast } from 'react-toastify'

interface PipelineDistributionFieldsProps {
    ownerUserId: number | null
    onOwnerChange: (ownerUserId: number | null) => void
    distributionGroupId: number | null
    onGroupChange: (distributionGroupId: number | null) => void
    /** Dona atual, para não sumir do select quando não está mais entre os candidatos. */
    currentOwner?: { id: number; name: string } | null
    /** Grupo já salvo no funil: ele já entra na contagem de funis do grupo. */
    savedGroupId?: number | null
    /** Chamado quando um grupo é renomeado ou excluído (os cards dos funis mudam). */
    onGroupsChanged?: () => void
}

type GroupEditMode = { kind: 'create' } | { kind: 'rename'; groupId: number } | null

/**
 * Dona e grupo de distribuição do funil — docs/sales-pipeline-distribution.md.
 */
export default function PipelineDistributionFields({
    ownerUserId,
    onOwnerChange,
    distributionGroupId,
    onGroupChange,
    currentOwner,
    savedGroupId,
    onGroupsChanged,
}: PipelineDistributionFieldsProps) {
    const [candidates, setCandidates] = useState<PipelineOwnerCandidate[]>([])
    const [groups, setGroups] = useState<PipelineDistributionGroup[]>([])
    const [loading, setLoading] = useState(true)

    const [editMode, setEditMode] = useState<GroupEditMode>(null)
    const [groupName, setGroupName] = useState('')
    const [savingGroup, setSavingGroup] = useState(false)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deletingGroup, setDeletingGroup] = useState(false)

    useEffect(() => {
        let cancelled = false
        Promise.all([getPipelineOwnerCandidates(), getPipelineDistributionGroups()])
            .then(([candidatesResponse, groupsResponse]) => {
                if (cancelled) return
                if (candidatesResponse.success) {
                    setCandidates(candidatesResponse.result || [])
                } else {
                    toast.error(candidatesResponse.error || 'Erro ao carregar usuários')
                }
                if (groupsResponse.success) {
                    setGroups(groupsResponse.result || [])
                } else {
                    toast.error(groupsResponse.error || 'Erro ao carregar grupos de distribuição')
                }
            })
            .catch(() => {
                if (!cancelled) toast.error('Erro ao carregar dados de distribuição')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [])

    const ownerOptions =
        currentOwner && !candidates.some(candidate => candidate.id === currentOwner.id)
            ? [{ id: currentOwner.id, name: currentOwner.name, email: '' }, ...candidates]
            : candidates

    const selectedGroup = groups.find(group => group.id === distributionGroupId) ?? null

    const startCreate = () => {
        setEditMode({ kind: 'create' })
        setGroupName('')
    }

    const startRename = () => {
        if (!selectedGroup) return
        setEditMode({ kind: 'rename', groupId: selectedGroup.id })
        setGroupName(selectedGroup.name)
    }

    const cancelEdit = () => {
        setEditMode(null)
        setGroupName('')
    }

    const saveGroup = async () => {
        const name = groupName.trim()
        if (!name || !editMode) return
        setSavingGroup(true)
        try {
            if (editMode.kind === 'create') {
                const response = await createPipelineDistributionGroup({ name })
                if (response.success && response.result) {
                    const created = response.result
                    setGroups(prev =>
                        [...prev, { ...created, _count: created._count ?? { pipelines: 0 } }].sort((a, b) =>
                            a.name.localeCompare(b.name, 'pt-BR')
                        )
                    )
                    onGroupChange(created.id)
                    toast.success('Grupo criado')
                    cancelEdit()
                } else {
                    toast.error(response.error || 'Erro ao criar grupo')
                }
            } else {
                const response = await updatePipelineDistributionGroup(editMode.groupId, { name })
                if (response.success) {
                    setGroups(prev =>
                        prev.map(group => (group.id === editMode.groupId ? { ...group, name } : group))
                    )
                    toast.success('Grupo renomeado')
                    cancelEdit()
                    onGroupsChanged?.()
                } else {
                    toast.error(response.error || 'Erro ao renomear grupo')
                }
            }
        } catch {
            toast.error('Erro ao salvar grupo')
        } finally {
            setSavingGroup(false)
        }
    }

    const confirmDeleteGroup = async () => {
        if (!selectedGroup) return
        setDeletingGroup(true)
        try {
            const response = await deletePipelineDistributionGroup(selectedGroup.id)
            if (response.success) {
                setGroups(prev => prev.filter(group => group.id !== selectedGroup.id))
                onGroupChange(null)
                setShowDeleteModal(false)
                toast.success('Grupo excluído')
                onGroupsChanged?.()
            } else {
                toast.error(response.error || 'Erro ao excluir grupo')
            }
        } catch {
            toast.error('Erro ao excluir grupo')
        } finally {
            setDeletingGroup(false)
        }
    }

    const otherPipelinesInGroup = selectedGroup
        ? Math.max(selectedGroup._count.pipelines - (savedGroupId === selectedGroup.id ? 1 : 0), 0)
        : 0

    return (
        <div className="pt-4 border-t border-base-300/50">
            <div className="flex items-center gap-2 mb-1">
                <MdShuffle className="text-lg text-secondary" />
                <h4 className="font-semibold text-base-content">Distribuição</h4>
            </div>
            <p className="text-xs text-base-content/60 leading-snug mb-4">
                Oportunidades que entram automaticamente (IA, fluxos, integrações e loja) são distribuídas
                entre os funis do mesmo grupo: primeiro o funil da dona da carteira do cliente, senão em
                rodízio. A dona vira responsável pela oportunidade e pelo lead, e o cliente sem carteira
                entra na carteira ativa dela.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text font-medium">Dona do funil</span>
                    </label>
                    <select
                        className="select select-bordered w-full"
                        value={ownerUserId ?? ''}
                        disabled={loading}
                        onChange={e => onOwnerChange(e.target.value ? parseInt(e.target.value) : null)}
                    >
                        <option value="">Sem dona</option>
                        {ownerOptions.map(candidate => (
                            <option key={candidate.id} value={candidate.id}>
                                {candidate.name}
                            </option>
                        ))}
                    </select>
                    <label className="label pt-1.5 pb-0 whitespace-normal">
                        <span className="label-text-alt text-base-content/60 whitespace-normal leading-snug">
                            A dona sempre vê o funil, mesmo quando ele é restrito a perfis.
                        </span>
                    </label>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text font-medium">Grupo de distribuição</span>
                    </label>

                    {editMode ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Nome do grupo"
                                value={groupName}
                                autoFocus
                                disabled={savingGroup}
                                onChange={e => setGroupName(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        saveGroup()
                                    }
                                    if (e.key === 'Escape') {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        cancelEdit()
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-secondary btn-square"
                                onClick={saveGroup}
                                disabled={savingGroup || !groupName.trim()}
                                title={editMode.kind === 'create' ? 'Criar grupo' : 'Salvar nome'}
                            >
                                {savingGroup ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    <MdCheck className="text-lg" />
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-square"
                                onClick={cancelEdit}
                                disabled={savingGroup}
                                title="Cancelar"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1">
                            <select
                                className="select select-bordered w-full min-w-0"
                                value={distributionGroupId ?? ''}
                                disabled={loading}
                                onChange={e => onGroupChange(e.target.value ? parseInt(e.target.value) : null)}
                            >
                                <option value="">Sem grupo</option>
                                {groups.map(group => (
                                    <option key={group.id} value={group.id}>
                                        {group.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn btn-ghost btn-square btn-sm"
                                onClick={startCreate}
                                disabled={loading}
                                title="Criar grupo"
                            >
                                <MdAdd className="text-lg" />
                            </button>
                            {selectedGroup && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-square btn-sm"
                                        onClick={startRename}
                                        title="Renomear grupo"
                                    >
                                        <MdEdit className="text-base" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-square btn-sm text-error hover:bg-error/10"
                                        onClick={() => setShowDeleteModal(true)}
                                        title="Excluir grupo"
                                    >
                                        <MdDelete className="text-base" />
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    <label className="label pt-1.5 pb-0 whitespace-normal">
                        <span className="label-text-alt text-base-content/60 whitespace-normal leading-snug">
                            {editMode?.kind === 'create'
                                ? 'O grupo novo já fica selecionado para este funil.'
                                : selectedGroup
                                  ? otherPipelinesInGroup > 0
                                      ? `Divide as oportunidades com mais ${otherPipelinesInGroup} ${otherPipelinesInGroup === 1 ? 'funil' : 'funis'} do grupo.`
                                      : 'Nenhum outro funil neste grupo ainda.'
                                  : 'Sem grupo, o funil recebe diretamente as oportunidades destinadas a ele.'}
                        </span>
                    </label>
                </div>
            </div>

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteGroup}
                title="Excluir grupo de distribuição"
                message={`Tem certeza de que deseja excluir o grupo "${selectedGroup?.name}"? Os funis do grupo ficam sem grupo e passam a receber as oportunidades diretamente.`}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deletingGroup}
            />
        </div>
    )
}
